import {
  CategorizedHPOOption,
  CollectionType,
  OrderFormInfoData,
  PhenotypicTrait,
  RequisitionFormData,
  Sex,
  TestType,
} from '@/types/RequisitionFormTypes'
import { PDFDocument, PDFForm } from 'pdf-lib'
import states from 'states-us'

// Helper function to convert CategorizedHPOOption[] to string with categories grouped
const formatHPOTerms = (hpoTerms: CategorizedHPOOption[] | undefined): string => {
  if (!hpoTerms || hpoTerms.length === 0) {
    return ''
  }

  // Group terms by category
  const groupedTerms = hpoTerms.reduce(
    (groups, term) => {
      const category = term.category || 'Key'
      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push(term)
      return groups
    },
    {} as Record<string, CategorizedHPOOption[]>
  )

  // Format each category group
  const categoryOrder = ['Key', 'Additional', 'Excluded']
  const formattedGroups = categoryOrder
    .filter((category) => groupedTerms[category] && groupedTerms[category].length > 0)
    .map((category) => {
      const terms = groupedTerms[category].map((term) => `${term.label}`).join('; ')
      return `${category}: ${terms}`
    })

  return formattedGroups.join(' | ')
}

export const renderTestRequisitionPDF = async (
  formData: RequisitionFormData,
  orderFormInfo: OrderFormInfoData
): Promise<Uint8Array> => {
  try {
    // Fetch the PDF template from public folder
    const response = await fetch('/forms/RCIGM-CGC-Test-Requisition-Form.pdf')
    if (!response.ok) {
      throw new Error('Failed to fetch PDF template')
    }

    const formPdfBytes = await response.arrayBuffer()
    const pdfDoc = await PDFDocument.load(formPdfBytes)

    const form = pdfDoc.getForm()
    populateForm(form, formData, orderFormInfo)

    // populatedForm.flatten(); // optional – makes fields uneditable
    const pdfBytes = await pdfDoc.save()
    return pdfBytes
  } catch (error) {
    console.error('Error rendering PDF:', error)
    throw error
  }
}

function formatDateForInput(YYYYMMDD_Date: string): string {
  if (!YYYYMMDD_Date) return ''

  // Parse the YYYY-MM-DD date string using local timezone to avoid off-by-one day issues
  const [year, month, day] = YYYYMMDD_Date.split('-').map(Number)

  // Check if the date components are valid
  if (isNaN(year) || isNaN(month) || isNaN(day)) return ''

  // Format as MM/DD/YYYY
  const formattedMonth = String(month).padStart(2, '0')
  const formattedDay = String(day).padStart(2, '0')

  return `${formattedMonth}/${formattedDay}/${year}`
}

function populateForm(form: PDFForm, formData: RequisitionFormData, orderFormInfo: OrderFormInfoData) {
  // Address operations to resolve state to abbreviation and country to USA if not provided
  const orgAddress = orderFormInfo.institution?.address
  const assumeUSA =
    orgAddress?.country === 'USA' ||
    orgAddress?.country === 'United States' ||
    orgAddress?.country === 'US' ||
    orgAddress?.country === 'United States of America' ||
    // If country is not provided, determine if state is in US
    (!orgAddress?.country &&
      (states.some((state) => state.abbreviation.toLowerCase() === orgAddress?.state?.toLowerCase()) ||
        states.some((state) => state.name.toLowerCase() === orgAddress?.state?.toLowerCase())))
  const orgState = assumeUSA
    ? (
        states.find((state) => state.abbreviation.toLowerCase() === orgAddress?.state?.toLowerCase()) ||
        states.find((state) => state.name.toLowerCase() === orgAddress?.state?.toLowerCase())
      )?.abbreviation
    : orgAddress?.state

  const textFieldMapping: Record<string, string | undefined> = {
    // Patient
    'Last Name 25': formData.patient.lastName,
    'Last Name 101': formData.patient.firstName,
    'Last Name 28': formData.patient.middleName,
    'Last Name 27': formData.patient.mrn,
    'Last Name 38': formatDateForInput(formData.patient.dateOfBirth),
    // Organization
    'Last Name 104': orderFormInfo.institution?.name,
    'Last Name 105': orgAddress?.street,
    'Last Name 106': orgAddress?.city,
    'Last Name 109': orgState,
    'Last Name 108': orgAddress?.zip,
    'Last Name 1010': assumeUSA ? 'USA' : orgAddress?.country,
    // Provider
    'Last Name 68': orderFormInfo.orderingProvider?.name.lastName,
    'Last Name 118': orderFormInfo.orderingProvider?.name.firstName,
    'Last Name 100': orderFormInfo.orderingProvider?.roleTitle,
    'Last Name 1011': orderFormInfo.orderingProvider?.phone,
    'Last Name 113': orderFormInfo.orderingProvider?.email,

    // Parents
    'Last Name 22': `${formData.mother?.parentFirstName || ''} ${formData.mother?.parentLastName || ''} ${formatDateForInput(formData.mother?.parentDateOfBirth || '')}`,
    'Last Name 62': formData.mother?.parentMRN,
    'First Name 8': formData.mother?.parentFirstName,
    'Last Name 46': formData.mother?.parentLastName,
    'DOB 8': formatDateForInput(formData.mother?.parentDateOfBirth ?? ''),
    'MRN/Patient ID 8': 'mother',
    'Last Name 44': formData.mother?.parentMRN,
    'Last Name 57': formatHPOTerms(formData.mother?.hpoTerms),

    'Last Name 23': `${formData.father?.parentFirstName || ''} ${formData.father?.parentLastName || ''} ${formatDateForInput(formData.father?.parentDateOfBirth || '')}`,
    'Last Name 70': formData.father?.parentMRN,
    'First Name 9': formData.father?.parentFirstName,
    'Last Name 48': formData.father?.parentLastName,
    'DOB 9': formatDateForInput(formData.father?.parentDateOfBirth ?? ''),
    'MRN/Patient ID 9': 'father',
    'Last Name 47': formData.father?.parentMRN,
    'Last Name 58': formatHPOTerms(formData.father?.hpoTerms),

    // Incidental Findings
    'Text Field 163': `${formData.mother?.parentFirstName || ''} ${formData.mother?.parentLastName || ''}`,
    'Text Field 164': `${formData.father?.parentFirstName || ''} ${formData.father?.parentLastName || ''}`,

    // Consent
    'Last Name 30': orderFormInfo.medicalProfessionalInfo.npi,
    'Last Name 31': orderFormInfo.medicalProfessionalInfo.name,
    // Clinical information
    'Clinical Diagnosis 5': formatHPOTerms(formData.reason.clinicalDiagnosis),
    'ICD-10 codes 6':
      typeof formData.reason.icd10Diagnosis === 'string'
        ? formData.reason.icd10Diagnosis
        : Array.isArray(formData.reason.icd10Diagnosis) && formData.reason.icd10Diagnosis.length > 0
          ? formData.reason.icd10Diagnosis.map((code) => code.id).join(', ')
          : '',
    'Indication for Testing 3': [
      formData.clinical.clinicalNote,
      formData.family.hasParent ||
      formData.family.hasGrandparent ||
      formData.family.hasSibling ||
      formData.family.hasCousin
        ? `Family member(s) with key phenotypes: ${[
            formData.family.hasParent ? 'Parent' : '',
            formData.family.hasGrandparent ? 'Grandparent' : '',
            formData.family.hasSibling ? 'Sibling' : '',
            formData.family.hasCousin ? 'Cousin' : '',
          ]
            .filter(Boolean)
            .join(', ')}`
        : '',
      formData.family.familyHistory,
    ].join('\n'),
  }

  for (const [key, value] of Object.entries(textFieldMapping)) {
    if (value?.trim()) {
      form.getTextField(key).setText(value || '')
    }
  }

  switch (
    formData.patient.sex // Patient Sex Checkbox
  ) {
    case Sex.Female:
      form.getCheckBox('Check Box 352').check()
      break
    case Sex.Male:
      form.getCheckBox('Check Box 353').check()
      break
    case Sex.Ambiguous: // Ambiguous and Unknown
      form.getCheckBox('Check Box 354').check()
      break
    default:
      // Invalid selection
      break
  }

  switch (
    formData.patient.collectionType // Patient Sample Type Checkbox
  ) {
    case CollectionType.WholeBloodEDTA:
      form.getCheckBox('Check Box 267').check()
      break
    default:
      // Invalid selection
      break
  }

  switch (
    formData.order.selectedOption // Test Type Checkbox
  ) {
    case TestType.urWGS:
      form.getCheckBox('Check Box 277').check()
      break
    case TestType.rWGS_Patient_Only:
      form.getCheckBox('Check Box 280').check()
      break
    case TestType.rWGS_Duo:
      form.getCheckBox('Check Box 281').check()
      break
    case TestType.rWGS_Trio:
      form.getCheckBox('Check Box 283').check()
      break
    default:
      // Invalid selection
      break
  }

  // Parent Information
  if (formData.mother) {
    form.getCheckBox('Check Box 294').check() // Mother Checkbox
    form.getCheckBox('Check Box 90').check() // Additional Family Member 1 Sex - Female
    switch (formData.mother.phenotypicTrait) {
      case PhenotypicTrait.ClinicallyUnaffected:
        form.getCheckBox('Check Box 86').check() // Additional Family Member 1 Phenotypic Trait - Clinically Unaffected
        break
      case PhenotypicTrait.ClinicallyAffected:
        form.getCheckBox('Check Box 88').check() // Additional Family Member 1 Phenotypic Trait - Clinically Affected
        break
      default:
        break
    }
    switch (formData.mother.collectionType) {
      case CollectionType.WholeBloodEDTA:
        form.getCheckBox('Check Box 158').check() // Mother Sample Type - Whole Blood EDTA
        break
      case CollectionType.Saliva:
        form.getCheckBox('Check Box 157').check() // Mother Sample Type - Saliva
        break
      default:
        break
    }
    form.getCheckBox('Check Box22').check() // Incidental Findings
  }
  if (formData.father) {
    form.getCheckBox('Check Box 295').check() // Father Checkbox
    form.getCheckBox('Check Box 320').check() // Additional Family Member 2 Sex - Male
    switch (formData.father.phenotypicTrait) {
      case PhenotypicTrait.ClinicallyUnaffected:
        form.getCheckBox('Check Box 317').check() // Additional Family Member 2 Phenotypic Trait - Clinically Unaffected
        break
      case PhenotypicTrait.ClinicallyAffected:
        form.getCheckBox('Check Box 318').check() // Additional Family Member 2 Phenotypic Trait - Clinically Affected
        break
      default:
        break
    }
    switch (formData.father.collectionType) {
      case CollectionType.WholeBloodEDTA:
        form.getCheckBox('Check Box 325').check() // Father Sample Type - Whole Blood EDTA
        break
      case CollectionType.Saliva:
        form.getCheckBox('Check Box 157').check() // Father Sample Type - Saliva
        break
      default:
        break
    }
    form.getCheckBox('Check Box23').check() // Incidental Findings
  }
}
